import { useState, useEffect } from "react";
import axios from "axios";
import { Play, X, ChevronRight } from "lucide-react";
import { API, apiService } from "../utils/api";
import { VideoPlayer } from "../components/shared/VideoPlayer";
import { Footer } from "../components/shared/Footer";

const FreeMoviesPage = () => {
  const [content, setContent] = useState([]);
  const [streamingServices, setStreamingServices] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [activeTab, setActiveTab] = useState('services');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, servicesRes] = await Promise.all([
          apiService.getPublicDomain(selectedGenre),
          axios.get(`${API}/free-streaming`)
        ]);
        setContent(contentRes.data);
        setStreamingServices(servicesRes.data.services);
        const uniqueGenres = [...new Set(contentRes.data.map(c => c.genre))];
        setGenres(uniqueGenres);
        setLoading(false);
      } catch (error) {
        console.error('Error:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedGenre]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-44 px-4 md:px-12" data-testid="free-movies-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Free TV & Movies</h1>
        <p className="text-gray-400">100% free and legal streaming - No subscription required</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-8 border-b border-gray-800 pb-4">
        <button
          onClick={() => setActiveTab('services')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'services' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          📺 Free Streaming Services
        </button>
        <button
          onClick={() => setActiveTab('movies')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            activeTab === 'movies' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          🎬 Classic Movies
        </button>
      </div>

      {/* Free Streaming Services Tab */}
      {activeTab === 'services' && (
        <div>
          <div className="bg-gradient-to-r from-red-900/30 to-gray-900 rounded-xl p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Free Live TV & On-Demand</h2>
            <p className="text-gray-300 mb-4">Click any service to start watching free content instantly</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
            {streamingServices.map(service => (
              <a
                key={service.id}
                href={service.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-900 rounded-xl p-6 hover:bg-gray-800 transition-all hover:scale-105 border border-gray-800 hover:border-red-600 group"
              >
                <div className="text-5xl mb-4">{service.logo}</div>
                <h3 className="text-white font-bold text-lg mb-1 group-hover:text-red-400">{service.name}</h3>
                <p className="text-gray-500 text-sm mb-3">{service.description}</p>
                <span className="inline-block bg-green-600/20 text-green-400 text-xs px-3 py-1 rounded-full font-medium">
                  FREE
                </span>
                <div className="mt-4 text-red-500 text-sm flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Watch Now <ChevronRight size={14} />
                </div>
              </a>
            ))}
          </div>

          {/* Featured Services */}
          <div className="mb-12">
            <h3 className="text-xl font-bold text-white mb-4">🌟 Top Picks for Free Live TV</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <ServiceFeatureCard 
                name="Pluto.tv" 
                description="250+ live channels including news, sports, movies & entertainment"
                url="https://pluto.tv"
                features={["Live TV", "Movies", "News", "Sports"]}
              />
              <ServiceFeatureCard 
                name="Tubi" 
                description="Thousands of free movies and TV shows on-demand"
                url="https://tubitv.com"
                features={["Movies", "TV Shows", "Originals"]}
              />
              <ServiceFeatureCard 
                name="The Roku Channel" 
                description="Free live TV and on-demand content"
                url="https://therokuchannel.roku.com"
                features={["Live TV", "Movies", "TV Shows"]}
              />
            </div>
          </div>
        </div>
      )}

      {/* Classic Movies Tab */}
      {activeTab === 'movies' && (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white mb-2">Public Domain Classic Films</h2>
            <p className="text-gray-400 text-sm">Free films from Internet Archive - Watch instantly</p>
          </div>

          {/* Genre Filter */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedGenre(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                !selectedGenre ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              All Genres
            </button>
            {genres.map(genre => (
              <button
                key={genre}
                onClick={() => setSelectedGenre(genre)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap capitalize ${
                  selectedGenre === genre ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {genre}
              </button>
            ))}
          </div>

          {/* Selected Movie Player */}
          {selectedMovie && (
            <div className="mb-8 bg-gray-900 rounded-xl p-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedMovie.title}</h2>
                  <p className="text-gray-400">{selectedMovie.year} • {selectedMovie.duration} min • {selectedMovie.genre}</p>
                </div>
                <button onClick={() => setSelectedMovie(null)} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              <VideoPlayer 
                source={selectedMovie.video_url}
                type="direct"
                title={selectedMovie.title}
                poster={selectedMovie.thumbnail}
                autoPlay={true}
              />
              <p className="text-gray-300 mt-4">{selectedMovie.description}</p>
              <a 
                href={`https://archive.org/details/${selectedMovie.archive_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-red-500 hover:text-red-400"
              >
                View on Internet Archive →
              </a>
            </div>
          )}

          {/* Movie Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {content.map(movie => (
              <div 
                key={movie.id}
                onClick={() => setSelectedMovie(movie)}
                className="cursor-pointer group"
              >
                <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2">
                  <img 
                    src={movie.thumbnail} 
                    alt={movie.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50">
                    <Play size={48} className="text-white" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <span className="text-xs text-red-500 font-medium">FREE</span>
                    <h4 className="text-white font-medium truncate">{movie.title}</h4>
                    <p className="text-gray-400 text-xs">{movie.year} • {movie.genre}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

// Service Feature Card Component
const ServiceFeatureCard = ({ name, description, url, features }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-red-600 transition-all group"
  >
    <h4 className="text-white font-bold text-lg mb-2 group-hover:text-red-400">{name}</h4>
    <p className="text-gray-400 text-sm mb-4">{description}</p>
    <div className="flex flex-wrap gap-2 mb-4">
      {features.map(f => (
        <span key={f} className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded">{f}</span>
      ))}
    </div>
    <span className="text-red-500 text-sm">Watch Free →</span>
  </a>
);

export default FreeMoviesPage;
